(() => {
  const root = document.querySelector('[data-ps-showcase]');
  const track = root?.querySelector('[data-ps-showcase-track]');
  if (!root || !track) return;

  const reals = [...track.querySelectorAll('[data-ps-showcase-slide]')];
  const n = reals.length;
  if (!n) return;

  const loop = root.dataset.loop !== 'false' && n > 1;
  const autoplay = root.dataset.autoplay === 'true' && n > 1;
  const speed = Math.max(2000, Number(root.dataset.speed) || 4000);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const dotsEl = root.querySelector('[data-ps-showcase-dots]');
  const prev = root.querySelector('[data-ps-showcase-prev]');
  const next = root.querySelector('[data-ps-showcase-next]');
  const panelTitle = root.querySelector('[data-ps-showcase-title]');
  const panelLink = root.querySelector('[data-ps-showcase-link]');
  const panelKicker = root.querySelector('[data-ps-showcase-kicker]');
  const panelDesc = root.querySelector('[data-ps-showcase-desc]');

  // Infinite loop: a full copy of the slides on each side; after settling on a copy we jump
  // (without animation) to the matching real slide, so the carousel never reaches an end.
  const makeClone = (slide) => {
    const clone = slide.cloneNode(true);
    clone.classList.add('is-clone');
    clone.classList.remove('is-active');
    clone.removeAttribute('aria-current');
    clone.setAttribute('aria-hidden', 'true');
    // not `inert`: that also blocks taps, so the half-visible copy beside the last/first product couldn't be tapped.
    // Copies stay out of the tab order and the accessibility tree instead.
    clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    return clone;
  };
  if (loop) {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    reals.forEach((slide) => { before.appendChild(makeClone(slide)); after.appendChild(makeClone(slide)); });
    track.insertBefore(before, reals[0]);
    track.appendChild(after);
  }
  const all = [...track.querySelectorAll('.ps-showcase__slide')];
  // Only a few distinct photos (copies reuse the same files): load them all now so no slide is blank when the loop wraps
  track.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  // ...and decode them up front, so a slide scrolled in for the first time (or reached via the loop) paints instantly
  const decodeAll = () => track.querySelectorAll('img').forEach((img) => {
    img.decoding = 'sync';
    if (img.decode) img.decode().catch(() => {});
  });
  if (document.readyState === 'complete') decodeAll();
  else window.addEventListener('load', decodeAll, { once: true });
  const offset = loop ? n : 0;
  const realIndex = (pos) => ((pos % n) + n) % n;

  let pos = offset + Math.floor(n / 2);
  let scrollLock = false;
  let scrollTimer;
  let settleTimer;

  const syncPanel = (slide) => {
    if (!slide) return;
    if (panelTitle) panelTitle.textContent = slide.getAttribute('data-title') || '';
    if (panelLink) panelLink.href = slide.getAttribute('data-url') || '#';
    if (panelKicker) {
      const kicker = slide.getAttribute('data-kicker') || '';
      panelKicker.textContent = kicker;
      panelKicker.hidden = !kicker;
    }
    if (panelDesc) panelDesc.textContent = slide.getAttribute('data-desc') || '';
  };

  const getNearestPos = () => {
    const center = track.scrollLeft + track.clientWidth / 2;
    let nearest = pos;
    let minDist = Infinity;
    all.forEach((slide, index) => {
      const dist = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
      if (dist < minDist) { minDist = dist; nearest = index; }
    });
    return nearest;
  };

  const scrollToPos = (p, smooth = true) => {
    const slide = all[p];
    if (!slide) return;
    const target = slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
    track.scrollTo({ left: Math.max(0, target), behavior: smooth ? 'smooth' : 'instant' });
  };

  const setActive = (p) => {
    pos = Math.min(Math.max(p, 0), all.length - 1);
    all.forEach((slide, i) => {
      const isActive = i === pos;
      slide.classList.toggle('is-active', isActive);
      if (isActive && !slide.classList.contains('is-clone')) slide.setAttribute('aria-current', 'true');
      else slide.removeAttribute('aria-current');
    });
    const r = realIndex(pos);
    dotsEl?.querySelectorAll('.carousel-dot').forEach((dot, i) => dot.classList.toggle('is-active', i === r));
    syncPanel(all[pos]);
  };

  // Settled on a copy → jump to the matching real slide with transitions off
  const normalize = () => {
    if (!loop || (pos >= n && pos < 2 * n)) return;
    const target = n + realIndex(pos);
    track.classList.add('is-jumping');
    setActive(target);
    scrollToPos(target, false);
    void track.offsetWidth;
    requestAnimationFrame(() => requestAnimationFrame(() => track.classList.remove('is-jumping')));
  };

  const goTo = (p, smooth = true) => {
    if (!loop) p = realIndex(p);
    scrollLock = true;
    setActive(p);
    scrollToPos(pos, smooth);
    clearTimeout(settleTimer);
    settleTimer = window.setTimeout(() => {
      scrollLock = false;
      setActive(getNearestPos());
      normalize();
    }, smooth ? 560 : 80);
  };

  reals.forEach((_, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'carousel-dot';
    dot.setAttribute('aria-label', `Show product ${index + 1}`);
    dot.addEventListener('click', () => {
      // shortest way round to the chosen product
      let delta = index - realIndex(pos);
      if (loop) { if (delta > n / 2) delta -= n; if (delta < -n / 2) delta += n; }
      goTo(pos + delta);
    });
    dotsEl?.appendChild(dot);
  });

  prev?.addEventListener('click', () => goTo(pos - 1));
  next?.addEventListener('click', () => goTo(pos + 1));

  const atc = root.querySelector('[data-ps-showcase-atc]');
  atc?.addEventListener('click', async () => {
    const variant = all[pos]?.getAttribute('data-variant');
    if (!variant) return;
    atc.classList.add('is-loading');
    atc.disabled = true;
    try {
      if (window.PsCart && typeof window.PsCart.add === 'function') {
        await window.PsCart.add(variant, 1);
      } else {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart/add';
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'id';
        input.value = variant;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        return;
      }
    } catch (err) {
      console.error('Showcase add to cart failed', err);
    } finally {
      atc.classList.remove('is-loading');
      atc.disabled = false;
    }
  });

  track.addEventListener('scroll', () => {
    if (scrollLock) return;
    clearTimeout(scrollTimer);
    scrollTimer = window.setTimeout(() => {
      setActive(getNearestPos());
      normalize();
    }, 140);
  }, { passive: true });

  all.forEach((slide, index) => {
    const link = slide.querySelector('[data-ps-showcase-visual-link]');
    const handleSlideClick = (event) => {
      if (!slide.classList.contains('is-active')) {
        event.preventDefault();
        goTo(index);
      }
    };
    slide.addEventListener('click', handleSlideClick);
    link?.addEventListener('click', handleSlideClick);
  });

  // Autoscroll (optional): pauses on hover, keyboard focus, touch, and when the tab is hidden
  if (autoplay && !reduceMotion) {
    let paused = false;
    let touchTimer;
    window.setInterval(() => {
      if (!paused && !document.hidden) goTo(pos + 1);
    }, speed);
    root.addEventListener('mouseenter', () => { paused = true; });
    root.addEventListener('mouseleave', () => { paused = false; });
    root.addEventListener('focusin', () => { paused = true; });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) paused = false; });
    root.addEventListener('touchstart', () => {
      paused = true;
      clearTimeout(touchTimer);
      touchTimer = window.setTimeout(() => { paused = false; }, speed * 2);
    }, { passive: true });
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      scrollToPos(pos, false);
      setActive(pos);
    }, 120);
  }, { passive: true });

  const init = () => {
    track.classList.add('is-jumping');
    setActive(pos);
    scrollToPos(pos, false);
    requestAnimationFrame(() => requestAnimationFrame(() => track.classList.remove('is-jumping')));
  };
  requestAnimationFrame(init);
  window.addEventListener('load', () => scrollToPos(pos, false), { once: true });
})();
